import {
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Theme,
  Tooltip,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import CustomTable, { IColumn } from "../../common/CustomTable/CustomTable";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState } from "react";
import {
  getAllAdminUsers,
  addNewAdmin,
  deleteAdmin,
  inviteAdminUsers,
  updateAdmin,
} from "./UserManagementActions";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import UserModal from "./UserModal/UserModal";
import { IAdminUser } from "../../models/admin/userManagement/userManagement";
import InviteUser from "./InviteUser/InviteUser";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import { getCookie } from "../../utils/Utility";
import { adminForgotPassword } from "../Login/adminAuthActions";

const AdminUsersCard = () => {
  const dispatch = useAppDispatch();
  const roleCode = getCookie("roleCode");
  const isMobile = useMediaQuery("(max-width:767px)");
  const { adminUsers } = useAppSelector(
    (state: RootState) => state.userManagement
  );

  const [openAddUserForm, setOpenAddUserForm] = useState<boolean>(false);
  const [isEditUser, setIsEditUser] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<IAdminUser | null>(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const handleAddUser = () => {
    setOpenAddUserForm(true);
  };

  const handleDeleteUser = async (key: string) => {
    if (key) {
      await dispatch(deleteAdmin(key));
    }
    dispatch(getAllAdminUsers());
  };

  const { isSuccess, calledReducerType, isError, isLoading } = useAppSelector(
    (state) => state.common
  );
  const handleEditUser = (email: string) => {
    const userToEdit = adminUsers?.find((user) => user?.email === email);
    setSelectedUser(userToEdit!);
    setIsEditUser(true);
  };

  const handleResetPassword = (email: string) => {
    dispatch(adminForgotPassword(email));
  };

  useEffect(() => {
    dispatch(getAllAdminUsers());
  }, [dispatch, updateAdmin, deleteAdmin]);

  const columns: IColumn[] = [
    { label: "Display Name", accessorKey: "displayName" },
    { label: "Email", accessorKey: "email" },
    {
      label: "Status",
      accessorKey: "status",
      Cell: ({ status }: any): JSX.Element => {
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              size="small"
              label={status && status?.toUpperCase()}
              color={
                status?.toLowerCase() === "active"
                  ? "success"
                  : status?.toLowerCase() === "invited"
                  ? "warning"
                  : "error"
              }
              sx={{ textAlign: "center" }}
            />
          </Box>
        );
      },
    },
    {
      label: "Actions",
      styles: { textAlign: "center" },
      //@ts-ignore
      Cell: <T extends SecretKey>(item: IAdminUser): JSX.Element => {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <Tooltip title="Edit">
              <IconButton
                disabled={roleCode === "1"}
                size="small"
                onClick={() => handleEditUser(item?.email)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                disabled={roleCode === "1"}
                size="small"
                onClick={() => handleDeleteUser(item?.email)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Reset Password">
              <IconButton
                // disabled={item?.email === "SmartIms@gmail.com"}
                disabled={true}
                size="small"
                onClick={() => handleResetPassword(item?.email)}
              >
                <LockResetOutlinedIcon />
              </IconButton>
            </Tooltip> */}
          </Box>
        );
      },
    },
    {
      label: "",
      accessorKey: "resetPassword",
      //@ts-ignore
      Cell: (item: IAdminUser): JSX.Element => {
        return (
          <>
            {item?.status?.toLowerCase() === "active" && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleResetPassword(item?.email)}
                disabled={item?.email === "admin@smartims.com"}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#F8F9FA",
                  border: "1px solid #DEE2E6",
                  color: "#000",
                  "&:hover": {
                    backgroundColor: "#000",
                    border: "1px solid #DEE2E6",
                  },
                }}
              >
                Reset Password
              </Button>
            )}
          </>
        );
      },
    },
  ];

  const handleInviteUser = async (emails: string[], roles: string[]) => {
    if (emails.length && roles.length) {
      await dispatch(inviteAdminUsers({ emails, roles }));
    }
  };

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
  };

  useEffect(() => {
    if (isError && calledReducerType === "adminUsers/inviteAdmin") {
      setMessageDialogOpen(true);
    }
    if (isSuccess && calledReducerType === "adminUsers/inviteAdmin") {
      dispatch(getAllAdminUsers());
    }
  }, [isSuccess, isError, calledReducerType, dispatch]);

  const handleSave = async (updatedUser: IAdminUser) => {
    const formData = new FormData();
    // Append user data to FormData
    Object.keys(updatedUser).forEach((key) => {
      const value = updatedUser[key as keyof IAdminUser];
      if (value !== "") {
        formData.append(key, value || "");
      }
    });

    if (selectedUser) {
      await dispatch(updateAdmin(formData));
    } else {
      // Call an action to add a new user
      await dispatch(addNewAdmin(formData));
    }

    dispatch(getAllAdminUsers());
    setIsEditUser(false);
  };

  const handleCloseUserModal = () => {
    setIsEditUser(false);
    setSelectedUser(null);
  };

  useEffect(() => {
    if (isSuccess && calledReducerType === "adminAuth/adminForgotPassword") {
      setSnackbarOpen(true);
    }
  }, [isSuccess, calledReducerType]);

  return (
    <>
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
      <Box
        id="user_management_container"
        data-test-id="user_management_container"
        aria-label="User Management Container"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingY: 2,
            // paddingX: 3,
          }}
          id="user_management_header"
          data-test-id="user_management_header"
          aria-label="User Management Header"
        >
          <Typography
            variant="h6"
            sx={{
              background: (theme: Theme) =>
                `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: "bold",
            }}
            id="user_management_title"
            data-test-id="user_management_title"
            aria-label="Manage Admin Users"
          >
            Manage Admin Users
          </Typography>
          <Button
            size={isMobile ? "small" : "medium"}
            variant="contained"
            sx={{ borderRadius: "4px" }}
            onClick={handleAddUser}
            id="user_management_invite_button"
            data-test-id="user_management_invite_button"
            aria-label="Invite New User"
          >
            + Invite User
          </Button>
        </Box>
        <Box
          sx={{ maxWidth: isMobile ? "calc(100vw - 48px)" : "none" }}
          id="user_management_table_container"
          data-test-id="user_management_table_container"
          aria-label="Users Table Container"
        >
          <CustomTable
            data={adminUsers}
            columns={columns}
            pagination
            stickyHeader
            displaySkeleton={
              isLoading && calledReducerType === "adminUsers/getAllAdminUsers"
            }
          />
        </Box>

        <UserModal
          open={isEditUser}
          onClose={handleCloseUserModal}
          onSave={handleSave}
          user={selectedUser}
        />
        <InviteUser
          open={openAddUserForm}
          onClose={() => setOpenAddUserForm(false)}
          onInvite={handleInviteUser}
        />
      </Box>
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        id="user_management_snackbar"
        data-test-id="user_management_snackbar"
        aria-label="Password Reset Notification"
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          id="user_management_alert"
          data-test-id="user_management_alert"
          aria-label="Password Reset Success Message"
        >
          Link sent to mail
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminUsersCard;
